import { ACCESS_MODEL, OTT_GLOBAL_PLAYER_ID } from '../constants';
import type { AccessModel, AppConfig, Config, GConfig } from '../../types/config';
import type { Settings } from '../../types/settings';
import type { LanguageDefinition } from '../../types/i18n';

import { createStore } from './utils';
import { branding_theme } from '../../../../platforms/web/src/config/themeConfig/branding';

type ConfigState = {
  loaded: boolean;
  config: Config;
  accessModel: AccessModel;
  settings: Settings;
  integrationType: string | null;
  supportedLanguages: LanguageDefinition[];
  featureFlags?: GConfig;
  appConfig: AppConfig;
  privacyPolicy: { en: string; es: string };
  termsOfUse: { en: string; es: string };
  analyticsConfig: any;
};

export const useConfigStore = createStore<ConfigState>('ConfigStore', () => ({
  loaded: false,
  featureFlags: {
    common: {
      default_language: 'en',
      supported_languages: ['en', 'es'],
      authentication_type: 'inplayer',
      subscription_type: '',
      cms_type: 'jwplayer',
      is_client_active: true,
      is_share_enabled: true,
      custom_base_api_url: '',
      legal_docs: {
        external_docs: {
          privacy_policy: '',
          terms_of_use: '',
          about_us: '',
          footer_support_link: '',
        },
        privacy_policy: {
          en: '',
          es: '',
        },
        terms_of_use: {
          en: '',
          es: '',
        },
        aboutUs: {
          en: '',
          es: '',
        },
        placement: {
          privacy_policy: [],
          terms_of_use: [],
          about_us: [],
          help: [],
        },
      },
      app_data: {
        support_email: '',
        support_email_type: '',
        footer_support_link: '',
      },
      multipart_upload_chunk_size: 15,
      thumbnail_size: 50,
    },
    inplayer: {
      client_id: '',
      drm_policy_id: '',
    },
    jwplayer: {
      config_id: '',
    },
    cleeng: {
      publisher_token: '',
      country: 'US',
      currency: 'USD',
      locale: 'en_US',
      max_no_of_continue_watching: '50',
      max_no_of_fav: '50',
      max_fav_message: {
        en: 'You reached maximum number of video listings',
        es: 'You reached maximum number of video listings',
      },
      api: {
        delete_account: '',
      },
      subscription: {
        is_renewable_enabled: true,
        is_subscription_change_allowed: false,
      },
    },
    authentication: {
      email_regex: '',
      is_authentication_enabled: true,
      is_qr_authentication_enabled: true,
      qr_settings: {
        show_registration_code: true,
        show_qr_image: false,
      },
      can_show_signin_button: true,
      can_show_signup_button: true,
      is_guest_mode_enabled: true,
      is_social_login_enabled: true,
      is_share_enabled: true,
      social_login_providers: {
        google: true,
        facebook: true,
        twitter: true,
        apple: true,
      },
      can_show_mobile_field: false,
    },
    subscription: {
      product_id_keys: {
        staging: '',
        production: '',
      },
    },
    feature_flags: {
      is_onboarding_screen_enabled: true,
      guest_streaming_enabled: false,
      ad_enabled: true,
      ad_settings: {
        ad_delivery_method: 'csai',
        ad_delivery_provider: 'jwp',
        ad_config_level: 'global',
      },
      is_subscription_enabled: true,
      show_offers_after_signup: false,
      is_search_enabled: true,
      is_favorite_enabled: true,
      is_continue_watching_enabled: false,
      is_recommended_content_enabled: true,
      is_download_enabled: true,
      is_upload_enabled: true,
      is_drm_enabled: true,
      drm_settings: {
        drm_config_id: 'string',
        drm_live_media_id: 'string',
        drm_playlist_id: 'string',
        drm_policy_id: 'string',
      },
      player_type: 'jwp',
      player_analytics_enabled: false,
      is_analytics_enabled: true,
      analytics_provider: {
        firebase: true,
        adobe: false,
        appsflyer: false,
        google: true,
      },
      is_crash_analytics: true,
      is_epg_enabled: true,
      epg_disabled_message: 'This feature is not available in this Device',
      epg_settings: {
        max_days: 1,
        epg_no_program_message: 'No broadcast available',
        epg_empty_slot_message: 'No scheduled programs',
        epg_display_hours: ['00:00', '23:59'],
        epg_time_format: '12h',
        epg_default_image: 'string',
      },
      settings: {
        is_privacy_policy_enabled: true,
        is_terms_of_use_enabled: true,
        is_about_us_enabled: true,
        is_switch_profile_enabled: false,
        is_settings_enable: false,
        is_video_quality_enabled: true,
        analytics_settings: {
          abobe: {
            script_tag: '',
          },
        },
        is_autoplay_enabled: true,
        autoplay_default_value: 'off',
      },
      is_support_email: '',
      is_multi_language_support: false,
      is_google_captcha_enabled: false,
    },
    theme_colors: branding_theme,
    ui_settings: {
      hero_banner: 'option1',
      ui_flags: {
        is_splitup_fullname: true,
        is_lastname_required: false,
      },
      hero_carousel: '',
    },
    platforms: {
      web: {
        is_client_active: true,
        latest_version: '1.0',
        activate_tv: true,
        is_share_enabled: true,
        export_account: true,
        delete_account: true,
        change_password: true,
        can_show_signin_button: true,
        can_show_signup_button: true,
        is_autoplay_enabled: true,
        gtm_id: '',
        ui_settings: {
          hero_banner: 'option1',
          ui_flags: {
            is_splitup_fullname: true,
            is_lastname_required: false,
          },
          hero_carousel: '',
        },
      },
    },
  },
  appConfig: {
    api: {
      base_url: '',
      end_point: {
        user: {
          get_user_profile: '',
          create_user_profile: '',
          delete_account: '',
          change_password: '',
          activate_tv: '',
        },
        favorite: {
          get_favorite: '',
          update_favorite: '',
          remove_favorite: '',
        },
        watch_history: {
          get_watch_history: '',
          update_watch_history: '',
          remove_watch_history: '',
        },
      },
    },
    feature: {
      isUtmEnabled: false,
    },
    lambda: {
      recaptcha_verification: '',
    },
    system73: {
      system73AccessKey: '',
    },
    branding: {
      logo_url: '',
      primary_font: '"Poppins", sans-serif',
      secondary_font: '"Poppins", sans-serif',
      favicon_url: '',
      client_site: '',
      support_email: '',
      video_horizontal_thumbnail: '',
      video_vertical_thumbnail: '',
      domain: '',
      footer_logo_url: '',
    },
    user_profile: {
      personal_details: {
        first_name_last_name: {
          enabled: true,
          required: false,
        },
        full_name: {
          enabled: true,
          required: false,
        },
        company_name: {
          enabled: false,
          required: false,
        },
        address: {
          enabled: false,
          required: false,
        },
        phone_number: {
          enabled: false,
          required: false,
        },
        birth_date: {
          enabled: false,
          required: false,
        },
      },
    },
    video_details: {
      epg: {
        is_catchup_hours_enabled: false,
      },
    },
    ui_controls: {
      button_display_modes: {
        label_only: true,
        icon_only: true,
        label_with_icon: true,
      },
      single_click: false,
      can_show_consent_management: true,
      cookies: {
        type: 'essential_only',
        description: {
          en: 'We use only essential cookies necessary for the proper functioning of this site. By continuing to use this site, you agree to their use.',
          es: 'Solo utilizamos cookies esenciales necesarias para el funcionamiento adecuado de este sitio. Al continuar usando este sitio, aceptas su uso.',
        },
        button_label: {
          en: 'I Accept',
          es: 'Acepto',
        },
        require_explicit_consent: true,
        consent_cookie_name: 'ott-cookies',
        consent_cookie_expiry_days: 365,
      },
      subscription: {
        payment_success_description: {
          en: 'Your payment was completed successfully. Welcome to your subscription!',
          es: 'Tu pago se ha completado con éxito. ¡Bienvenido a tu suscripción!',
        },
        payment_cancelled_description: {
          en: 'You have cancelled the payment',
          es: 'Has cancelado el pago',
        },
        payment_failed_description: {
          en: 'Payment failed, please try again',
          es: 'El pago ha fallado, por favor inténtalo de nuevo',
        },
      },
    },
    footer: {
      mobile_apps_links: {
        title: {
          en: '',
          es: '',
        },
        android: {
          enabled: true,
          icon_url: '',
          app_url: '',
        },
        ios: {
          enabled: true,
          icon_url: '',
          app_url: '',
        },
      },
    },
  },
  config: {
    id: '',
    siteName: '',
    description: '',
    player: '',
    siteId: '',
    contentType: '',
    assets: {},
    content: [],
    menu: [],
    custom: {},
    integrations: {
      cleeng: {
        useSandbox: true,
      },
      jwp: {
        clientId: null,
        assetId: null,
        useSandbox: true,
      },
    },
    styling: {},
  },
  analyticsConfig: {},
  settings: {
    additionalAllowedConfigSources: [],
    playerId: OTT_GLOBAL_PLAYER_ID,
  },
  supportedLanguages: [],
  accessModel: ACCESS_MODEL.AVOD,
  integrationType: null,
  privacyPolicy: { en: '', es: '' },
  termsOfUse: { en: '', es: '' },
}));
